import { useState } from 'react';
import { ChevronDown, Copy } from 'lucide-react';
import { AlertStatusChip, AlertTypeLabel } from '@/components/domain/AlertStatusChip';
import { EmptyState } from '@/components/states/EmptyState';
import { BellOff } from 'lucide-react';
import { absoluteTime } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { Alert } from '@/types/api';

/**
 * How an alert actually went out, per channel.
 *
 * `last_error` is the whole point of this table. When an alert fails, the reason
 * the provider gave is the difference between "the number is wrong" and "our Twilio
 * credentials expired", and reading it should not require server log access.
 *
 * There is no recipient column and there must never be one: the API withholds the
 * target phone number and push token deliberately, because they are personal data.
 */
export function AlertDeliveryTable({ alerts }: { alerts: Alert[] }) {
  const [expanded, setExpanded] = useState<number | null>(null);

  if (alerts.length === 0) {
    return (
      <EmptyState
        icon={BellOff}
        title="No alerts recorded"
        description="Nothing was dispatched for this event. Minor impacts notify the owner only."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead className="border-b border-line text-left text-ink-muted">
          <tr>
            <th className="px-4 py-2 font-medium">Channel</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2 font-medium">Attempts</th>
            <th className="hidden px-4 py-2 font-medium sm:table-cell">Last attempt</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {alerts.map((alert) => {
            const open = expanded === alert.id;
            const hasError = Boolean(alert.last_error);

            return (
              <tr key={alert.id} className="align-top">
                <td className="px-4 py-2.5">
                  <AlertTypeLabel type={alert.alert_type} />
                </td>
                <td className="px-4 py-2.5">
                  <AlertStatusChip status={alert.status} />
                  {hasError && (
                    <>
                      <button
                        onClick={() => setExpanded(open ? null : alert.id)}
                        aria-expanded={open}
                        className="mt-1.5 flex items-center gap-1 text-[12px] font-medium text-bad hover:underline"
                      >
                        <ChevronDown
                          className={cn('size-3 transition-transform', open && 'rotate-180')}
                          aria-hidden
                        />
                        {open ? 'Hide reason' : 'Why did this fail?'}
                      </button>

                      {open && (
                        <div className="mt-2 max-w-md rounded-[--radius-control] border border-line bg-surface-sunken p-2.5">
                          <pre className="whitespace-pre-wrap break-words font-mono text-[12px] leading-relaxed text-ink-secondary">
                            {alert.last_error}
                          </pre>
                          <button
                            onClick={() =>
                              void navigator.clipboard
                                .writeText(alert.last_error ?? '')
                                .catch(() => undefined)
                            }
                            className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-accent hover:underline"
                          >
                            <Copy className="size-3" aria-hidden />
                            Copy for support
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </td>
                <td className="px-4 py-2.5 text-ink" data-numeric>
                  {alert.attempts}
                </td>
                <td className="hidden whitespace-nowrap px-4 py-2.5 text-ink-secondary sm:table-cell">
                  {absoluteTime(alert.sent_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
